import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { CheckCircle2, ArrowRight, Calendar, Users, Crown, Gift, ShoppingBag, Mail, Shield, UserPlus, LogIn, Loader2, Download, Printer } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../context/UserContext';
import { useCart } from '../context/CartContext';
import { auth, db } from '../firebase';
import { doc, updateDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { jsPDF } from 'jspdf';

export default function SuccessPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [type, setType] = useState<'booking' | 'membership' | 'gift' | 'shop'>('booking');
  const service = searchParams.get('service');
  const planId = searchParams.get('plan_id');

  const [clientEmail, setClientEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState('');
  
  const [giftData, setGiftData] = useState<any>(null);
  const [loadingGift, setLoadingGift] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    let currentType: 'booking' | 'membership' | 'gift' | 'shop' = 'booking';
    
    if (path.includes('membership')) currentType = 'membership';
    else if (path.includes('gift')) currentType = 'gift';
    else if (path.includes('shop')) currentType = 'shop';
    else currentType = 'booking';
    
    setType(currentType);

    // Clear cart if this was a shop, booking, or membership purchase
    if (currentType === 'shop' || currentType === 'booking' || currentType === 'membership') {
      console.log(`SuccessPage: Clearing ${currentType} cart`);
      clearCart();
    }

    if (currentType === 'membership' && user && planId) {
       // Auto-grant the membership to the user who paid
       console.log('SuccessPage: Granting membership', planId, 'to', user.email);
       let membershipTier = 'Syndicate Member';
       if (planId === 'prospect-tier') membershipTier = 'Prospect';
       if (planId === 'syndicate-elite') membershipTier = 'Elite Status';

       const automationTags = [];
       if (planId === 'syndicate-ultimate') automationTags.push('SYNDICATE_MEMBER');
       if (planId === 'syndicate-elite') automationTags.push('SYNDICATE_MEMBER', 'ELITE_RANK');

       // Set hardwareCredit false by default since boards are no longer included
       updateDoc(doc(db, 'users', user.uid), {
         membershipStatus: 'active',
         membershipTier: membershipTier,
         membershipId: planId,
         hardwareCredit: false,
         automationTags: automationTags,
         role: 'member',
         waiverSigned: true
       }).catch(e => console.error('Failed to grant membership:', e));
    }

    // Self-healing session verification
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      console.log('SuccessPage: Verifying session', sessionId);
      fetch('/api/verify-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, userId: user?.uid })
      })
      .then(res => res.json())
      .then(async data => {
        console.log('SuccessPage: Session verification result', data);
        const resolvedEmail = data.customer_email || data.metadata?.email;
        if (resolvedEmail) {
          setClientEmail(resolvedEmail);
          
          // No auto background registration anymore. Guest will set password explicitly below.
        }
      })
      .catch(err => console.error('SuccessPage: Session verification error', err));

      if (currentType === 'gift') {
        setLoadingGift(true);
        let retries = 0;
        const maxRetries = 20; // 20 * 3s = 60s
        
        const fetchGiftCard = () => {
          fetch(`/api/gift-card-by-session/${sessionId}`)
            .then(res => res.json())
            .then(data => {
              if (data.success) {
                setGiftData(data.gift);
                setLoadingGift(false);
              } else {
                if (retries < maxRetries) {
                  retries++;
                  setTimeout(fetchGiftCard, 3000);
                } else {
                  setLoadingGift(false);
                }
              }
            })
            .catch(err => {
              console.error('Failed to fetch gift card', err);
              if (retries < maxRetries) {
                retries++;
                setTimeout(fetchGiftCard, 3000);
              } else {
                setLoadingGift(false);
              }
            });
        };
        
        fetchGiftCard();
      }
    }
  }, [searchParams]);

  const downloadPDF = () => {
    if (!giftData) return;
    
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [200, 100]
    });

    // Dark Background
    doc.setFillColor(20, 20, 20);
    doc.rect(0, 0, 200, 100, 'F');
    
    // Teal Accent
    doc.setDrawColor(26, 191, 171);
    doc.setLineWidth(1);
    doc.rect(5, 5, 190, 90, 'D');

    // Text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('OPEN WATER SYNDICATE', 100, 25, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text('GIFT OF THE WATER', 100, 35, { align: 'center' });

    doc.setFontSize(14);
    doc.text(`VALUE: $${giftData.amount}`, 100, 50, { align: 'center' });
    doc.text(`FOR: ${giftData.recipientName}`, 100, 60, { align: 'center' });
    
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 191, 171);
    doc.text(giftData.code, 100, 80, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text('REDEEM AT OPENWATERSYNDICATE.COM', 100, 92, { align: 'center' });

    doc.save(`OWS-GiftCard-${giftData.code}.pdf`);
  };

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientEmail || !password) {
      setCreationError('Email and password required.');
      return;
    }

    setIsCreating(true);
    setCreationError('');

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, clientEmail, password);
      
      // Determine appropriate membership tier based on planId or fallback to Prospect
      // Standard paid classes, group classes, or corporate events act as high-trust verification, granting Prospect.
      let membershipTier = 'Prospect';
      let membershipStatus = 'active' as const;
      let membershipId = '';
      let automationTags: string[] = [];
      let waiverSigned = false;

      if (type === 'membership') {
        membershipTier = 'Syndicate Member';
        if (planId === 'prospect-tier') {
          membershipTier = 'Prospect';
        } else if (planId === 'syndicate-elite') {
          membershipTier = 'Elite Status';
          automationTags = ['SYNDICATE_MEMBER', 'ELITE_RANK'];
        } else if (planId === 'syndicate-ultimate') {
          automationTags = ['SYNDICATE_MEMBER'];
        }
        membershipId = planId || '';
        waiverSigned = true; // Accepted during membership signup
      }

      // Fetch checkout details from local or Stripe session via verify-checkout-session to get client's real name
      let checkoutDisplayName = 'Explorer';
      try {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
          const vRes = await fetch('/api/verify-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, userId: userCredential.user.uid })
          });
          if (vRes.ok) {
            const vData = await vRes.json();
            checkoutDisplayName = vData.metadata?.clientName || vData.customer_details?.name || 'Explorer';
          }
        }
      } catch (err) {
        console.error('Failed to resolve checkout display name:', err);
      }

      // Create user profile document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        role: 'member',
        membershipTier,
        membershipStatus,
        membershipId,
        automationTags,
        email: clientEmail,
        displayName: checkoutDisplayName,
        waiverSigned,
        joinedAt: new Date().toISOString()
      }, { merge: true });

      // Dispatch welcome notification via secure SMTP backend
      try {
        await fetch('/api/send-prospect-welcome', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: clientEmail,
            displayName: checkoutDisplayName
          })
        });
      } catch (emailErr) {
        console.error('Failed to dispatch SMTP welcome:', emailErr);
      }

      // Attempt to claim guest data
      try {
        await fetch('/api/claim-guest-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: clientEmail, uid: userCredential.user.uid })
        });
      } catch (err) {
        console.error("Failed to claim guest data", err);
      }

      navigate('/member-portal');
    } catch (err: any) {
      console.error('Account creation error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setCreationError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setCreationError('Password should be at least 6 characters.');
      } else {
        setCreationError('Failed to create account. ' + err.message);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getDetails = () => {
    const isPreOrder = searchParams.get("preorder") === "true";
    const normalizedService = service?.toLowerCase().trim();

    if (normalizedService === "coaching") {
      return {
        icon: <Calendar className="h-12 w-12 text-ows-teal" />,
        title: "Private Coaching Confirmed",
        message: `Your one-on-one session for ${service} is secured. We've assigned your head coach and locked your calendar window.`,
        actionText: "Prepare Your Profile",
        actionLink: "/member-portal",
        benefit:
          "Private Focus: Personal instructions and gear checklist sent to portal.",
      };
    }

    if (normalizedService === "group lesson") {
      return {
        icon: <Users className="h-12 w-12 text-ows-teal" />,
        title: "Group Session Secured",
        message: `Your crew's spot for ${service} is now locked. Bring optimal gear and ensure your full squad registers their profiles.`,
        actionText: "Manage Your Squad",
        actionLink: "/member-portal",
        benefit:
          "Squad Sync: Distribute the digital waiver link to team members.",
      };
    }

    switch (type) {
      case "membership":
        return {
          icon: <Crown className="h-12 w-12 text-ows-teal" />,
          title: "Syndicate Activated",
          message:
            "Your credentials have been verified. You now have full access to our global expedition network.",
          actionText: "Enter Member Portal",
          actionLink: "/member-portal",
          benefit:
            "Your referral code is now live. Access it in the portal.",
        };

      case "gift":
        return {
          icon: <Gift className="h-12 w-12 text-ows-teal" />,
          title: "Gift of the Water",
          message:
            "The digital code has been dispatched. They'll receive it in their inbox shortly.",
          actionText: "Back to Home",
          actionLink: "/",
          benefit: "Receipt has been sent to your email.",
        };

      case "shop":
        return {
          icon: <ShoppingBag className="h-12 w-12 text-ows-teal" />,
          title: isPreOrder ? "Pre-Order Secured" : "Dispatch Confirmed",
          message: isPreOrder
            ? "Your spot in Batch 001 is locked. Your contribution funds the production line. We'll alert your terminal when the hull is ready for shipment."
            : "Your gear is being prepared for transport. We'll send tracking information as soon as it clears the warehouse.",
          actionText: "Continue Exploring",
          actionLink: "/",
          benefit: isPreOrder
            ? `Production Slot: #00${Math.floor(Math.random() * 9) + 1} Assigned. Check portal for status updates.`
            : "Syndicate Clearance: Purchase verified. Track status in your member terminal.",
        };

      default:
        return {
          icon: <Calendar className="h-12 w-12 text-ows-teal" />,
          title: "Booking Confirmed",
          message: `Your spot for ${service || "the session"} is secured. We've sent a calendar invite, achievement certificate, and waiver link to your email.`,
          actionText: user ? "Enter Member Portal" : "Back to Home",
          actionLink: user ? "/member-portal" : "/",
          benefit:
            "Mission Readiness: Digital waiver required before field deployment.",
        };
    }
  };

  const details = getDetails();

  return (
    <div className="min-h-screen bg-ows-sand pt-32 pb-20 px-4">
      <Helmet>
        <title>Success | Open Water Syndicate</title>
      </Helmet>

      <div className="max-w-2xl mx-auto space-y-12">
        {(user || type === 'gift') && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-12 rounded-[3.5rem] shadow-2xl border border-ows-teal/5 text-center relative overflow-hidden"
          >
            {/* Background Accent */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-ows-teal/5 rounded-full blur-3xl -mr-32 -mt-32" />
          
          <div className="relative z-10">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10, stiffness: 100, delay: 0.2 }}
              className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-10"
            >
              {details.icon}
            </motion.div>

            <h1 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
              {details.title}
            </h1>

            <p className="text-lg text-ows-dark/60 italic font-serif mb-12">
              "{details.message}"
            </p>

            <div className="space-y-6 mb-12">
              <div className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-dark/5 flex items-center gap-4 text-left shadow-sm">
                <Shield className="h-6 w-6 text-ows-teal shrink-0" />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/60 leading-relaxed font-mono">
                    {details.benefit}
                  </p>
                  <p className="text-[8px] text-ows-dark/30 uppercase tracking-[0.2em] mt-1 font-mono">Identity & Privileges Verified</p>
                </div>
              </div>
              
              <div className="flex flex-col items-center justify-center gap-3 text-ows-teal">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] font-mono">Secure Transmission Complete</span>
              </div>
            </div>

            <div className="mb-12 p-8 bg-ows-dark text-white rounded-3xl border border-ows-teal/20 text-left">
              {type === 'gift' ? (
                <div className="space-y-6">
                  <h4 className="text-xl font-display uppercase tracking-widest text-ows-teal mb-4 flex items-center gap-3">
                    <Gift className="h-5 w-5" /> Your Digital Code
                  </h4>
                  <div className="p-8 bg-[#141414] rounded-2xl border border-ows-teal/30 text-center space-y-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Unique Access Protocol</p>
                    {loadingGift ? (
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-ows-teal" />
                    ) : giftData ? (
                      <div className="space-y-6">
                        <p className="text-3xl font-mono font-bold tracking-[0.2em] text-ows-teal">{giftData.code}</p>
                        <div className="pt-4 border-t border-white/5">
                           <p className="text-[10px] text-white/60 mb-2 font-mono uppercase">Value: ${giftData.amount}</p>
                           <p className="text-[10px] text-white/60 font-mono uppercase">Recipient: {giftData.recipientName}</p>
                        </div>
                        <button 
                          onClick={downloadPDF}
                          className="flex items-center justify-center gap-2 w-full py-4 bg-white/5 text-white border border-white/10 rounded-xl hover:bg-white/10 transition-all font-mono text-[10px] uppercase tracking-widest"
                        >
                          <Download className="h-4 w-4" /> Download Digital Card
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm font-mono text-ows-orange">Awaiting protocol confirmation...</p>
                    )}
                  </div>
                  <p className="text-[10px] font-light text-white/60 italic text-center">
                    "The recipient has also been notified via secure dispatch."
                  </p>
                </div>
              ) : (
                <>
                  <h4 className="text-xl font-display uppercase tracking-widest text-ows-teal mb-4 flex items-center gap-3">
                    <Shield className="h-5 w-5" /> Immediate Action Required
                  </h4>
                  <p className="text-sm font-light text-white/80 mb-6 italic">
                    "No emails will be sent. Your deployment packet is secured in the Member Portal. Before you proceed, complete your mandatory clearance."
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <div className="p-4 bg-[#141414] rounded-2xl border border-white/5 flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Access Code</p>
                        <p className="text-sm font-mono tracking-wider text-ows-teal pt-1">ORION-{Math.random().toString(36).substring(2, 7).toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-[#141414] rounded-2xl border border-white/5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Liability Waiver</p>
                      <Link to="/member-portal" className="block w-full py-4 bg-ows-teal text-white font-bold text-[10px] uppercase tracking-[0.3em] text-center rounded-xl hover:bg-white hover:text-ows-dark transition-colors">
                        Sign Digital Waiver in Portal
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Link 
              to={details.actionLink}
              id="success-primary-action"
              className="w-full py-6 bg-ows-dark text-white rounded-2xl font-bold text-[10px] tracking-[0.5em] uppercase hover:bg-ows-teal transition-all flex items-center justify-center gap-4 group font-mono shadow-2xl hover:shadow-ows-teal/20"
            >
              {details.actionText}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
            </Link>

            <div className="mt-8 flex items-center justify-center gap-2 text-ows-dark/20 uppercase text-[8px] font-bold tracking-widest font-mono">
              <Shield className="h-3 w-3" />
              All dispatches sent directly to portal
            </div>
          </div>
        </motion.div>
        )}

        {/* Secure Account Prompt for Guests */}
        {!user && type !== 'gift' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-ows-dark p-12 rounded-[3.5rem] text-white shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-12 opacity-10">
              <Shield className="h-32 w-32" />
            </div>
            
            <div className="relative z-10 text-center space-y-8">
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-ows-teal/20 rounded-full border border-ows-teal/30">
                <Shield className="h-4 w-4 text-ows-teal" />
                <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-ows-teal">Identity Verification Complete</span>
              </div>
              
              <h3 className="text-3xl font-display font-light uppercase tracking-widest leading-tight">
                Secure Your <span className="text-ows-teal">Mission Profile</span>
              </h3>
              
              <p className="text-sm text-white/60 font-mono uppercase tracking-tighter max-w-sm mx-auto leading-relaxed">
                Your payment confirmed your status. Now, link your global identity to track shipments, manage bookings, and access the Syndicate's classified logs.
              </p>
              
              <form onSubmit={handleCreateProfile} className="max-w-sm mx-auto space-y-4 pt-4 text-left">
                <div>
                  <label className="block text-[10px] font-bold tracking-widest uppercase text-white/60 mb-2">Email</label>
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="ENTER EMAIL"
                    disabled={true}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white placeholder-white/30 focus:outline-none focus:border-ows-teal cursor-not-allowed"
                    required
                  />
                  {!clientEmail && (
                    <p className="text-[10px] text-ows-orange mt-1">Pending email recovery from secure payload...</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-widest uppercase text-white/60 mb-2">Create Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white placeholder-white/30 focus:outline-none focus:border-ows-teal"
                    required
                    minLength={6}
                  />
                </div>
                
                {creationError && (
                  <div className="text-[10px] text-ows-orange font-mono uppercase tracking-wide bg-ows-orange/10 p-3 rounded-lg border border-ows-orange/20">
                    {creationError}
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={isCreating || !clientEmail}
                  className="w-full mt-4 px-10 py-5 bg-ows-teal text-white rounded-2xl font-bold text-[10px] tracking-[0.4em] uppercase hover:bg-white hover:text-ows-dark transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} 
                  Activate & Enter Portal
                </button>
                
                <div className="pt-4 border-t border-white/10 mt-6 text-center">
                  <p className="text-[10px] text-white/60 font-mono mb-3">Already have an account?</p>
                  <Link 
                    to="/login"
                    className="px-6 py-3 bg-white/5 text-white rounded-xl font-bold text-[10px] tracking-[0.4em] uppercase hover:bg-white/10 transition-all inline-flex items-center justify-center gap-2 border border-white/10"
                  >
                    <LogIn className="h-3 w-3" /> Sign In Here
                  </Link>
                </div>
              </form>
              
              <p className="text-[8px] text-white/20 uppercase tracking-[0.5em] font-bold pt-4">
                Verification Protocol: B01-Success
              </p>
            </div>
          </motion.div>
        )}

        {/* Support Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <p className="text-[10px] text-ows-dark/40 uppercase tracking-widest leading-relaxed">
            Trouble with your transaction? <br />
            <a href="mailto:support@openwatersyndicate.com" className="text-ows-teal hover:underline mt-1 block">contact@openwatersyndicate.com</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
